import { Mutex } from "async-mutex";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import smileRingtoneUri from "../../assets/audio/smile-ringtone.mp3?url";
import { gracefullyPlayAudio } from "../../audio";
import { NOTIFICATION_VOLUME } from "../../config";
import { logger } from "../../datadog/logger";
import { useGetTelehealthSessionInfoCallback } from "../../hooks";
import { twilioSyncClientAtom } from "../../session";
import { useConnectRoomCallback } from "../../session/useConnectRoomCallback";
import { delayedEntryGate } from "../../statsig/gates";
import { Statsig } from "../../statsig/StatsigProvider";
import { toast } from "../../toasts";
import { debounce } from "../../utils";
import { roomTokenAtom } from "../../video-sdk";
import { zoomTokenAtom } from "../../video-sdk/zoom";
import { NEW_MESSAGE_TOAST_TAG } from "../chat/useShowNewMessageToastCallback";
import { setDefaultMeetingRoomFaviconAndTitle } from "../meeting-room/metadata";
import {
  activeConversationSidAtom,
  apiTokenAtom,
  preSessionVibesOpenAtom,
  sessionAppointmentShortIdAtom,
  visitorAccessDocSidAtom,
  VisitorState,
  visitorStateAtom,
} from "../state";
import { useVisitorId } from "../useVisitorId";
import {
  deadlineAtom,
  sessionConversationSidAtom,
} from "./delayed-entry/state";
import { usePreSessionPromptState } from "./pre-session-prompt/usePreSessionPromptState";
import { RoomValidationError } from "../../video-sdk/core/utils";

const LOAD_TIME_MS = 1000;
const DEBOUNCE_TIME_MS = 1000;

enum VisitorAccessDocEventType {
  GRANTED = "access-granted",
  DENIED = "access-denied",
}

type VisitorAccessDocData = {
  eventType: VisitorAccessDocEventType;
  token: string;
  /**
   * A token that can be used to communicate with the Telehealth API as a client
   * user
   */
  apiToken?: string;
  conversationSid?: string;
  deadline?: string;
  zoomJwt: string;
};

const playJoiningRoomSound = debounce(() => {
  const ringtone = new Audio(smileRingtoneUri);
  ringtone.volume = NOTIFICATION_VOLUME;
  gracefullyPlayAudio(ringtone);
}, DEBOUNCE_TIME_MS);

const showErrorToast = (message: string) => {
  toast.error(message, { purpose: "system" });
};

type HookOptions = {
  onDone?: () => void;
};

/**
 * Gracefully fetches the session appointment short ID and sets it in Jotai
 */
function useSetSessionAppointmentShortIdCallback() {
  const { providerShortId } = useParams<{ providerShortId: string }>();
  const { patientShortId } = useParams<{ patientShortId: string }>();
  const setSessionAppointmentShortId = useSetAtom(
    sessionAppointmentShortIdAtom,
  );
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();

  return useCallback(async () => {
    try {
      const { data } = await getTelehealthSessionInfo({
        variables: {
          patientShortId,
          providerShortId,
        },
      });
      setSessionAppointmentShortId(
        data?.telehealthSessionInfo?.appointment?.shortId,
      );
    } catch (error) {
      logger.error(
        "Failed to fetch/set appointment short ID for session",
        {
          patientShortId,
          providerShortId,
        },
        error as Error,
      );
    }
  }, [
    getTelehealthSessionInfo,
    patientShortId,
    providerShortId,
    setSessionAppointmentShortId,
  ]);
}

const roomMutex = new Mutex();
export function useTransitionToMeetingRoom() {
  const setVisitorStateAtom = useSetAtom(visitorStateAtom);
  const setActiveConversationSid = useSetAtom(activeConversationSidAtom);
  const setDeadline = useSetAtom(deadlineAtom);
  const [connectRoom] = useConnectRoomCallback();
  const setPresessionVibesOpen = useSetAtom(preSessionVibesOpenAtom);
  const { clearPreSessionPrompt } = usePreSessionPromptState();

  return useCallback(
    (conversationSid?: string, { onDone }: HookOptions = {}) => {
      // if multiple calls to transition to the meeting room happen simulatenously
      // the first one "wins" and the rest can just no-op.
      if (roomMutex.isLocked()) return;

      roomMutex.runExclusive(async () => {
        playJoiningRoomSound();
        setVisitorStateAtom(VisitorState.LOADING);

        // effectively sleep, need to await this way so that setTimeout runs async
        // otherwise, fire-and-forget will release the mutex before this timeout has elapsed!
        await new Promise((resolve) => setTimeout(resolve, LOAD_TIME_MS));

        const room = await connectRoom({
          onError: (error) => {
            if (
              error instanceof RoomValidationError &&
              error.kind === "empty-room"
            ) {
              showErrorToast("Your provider may have left the room");
              logger.warn("Room has less than 1 remote participant");
              return;
            }
            showErrorToast("There was an issue joining the meeting");
            logger.error("Failed to join room", {}, error as Error);
          },
        });

        if (!room) {
          setVisitorStateAtom(VisitorState.WAITING);
          return;
        }

        setVisitorStateAtom(VisitorState.MEETING);
        setDeadline(null);
        setPresessionVibesOpen(false);
        if (conversationSid) {
          toast.closeByTag(NEW_MESSAGE_TOAST_TAG);
          setActiveConversationSid(conversationSid);
        }

        clearPreSessionPrompt();
        setDefaultMeetingRoomFaviconAndTitle();
        onDone?.();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectRoom],
  );
}

export function useWatchForRoomToken() {
  const visitorUuid = useVisitorId();
  const syncClient = useAtomValue(twilioSyncClientAtom);
  const visitorAccessDocSid = useAtomValue(visitorAccessDocSidAtom);
  const setDeadline = useSetAtom(deadlineAtom);
  const transitionToMeetingRoom = useTransitionToMeetingRoom();
  const setRoomToken = useSetAtom(roomTokenAtom);
  const setZoomToken = useSetAtom(zoomTokenAtom);
  const setApiToken = useSetAtom(apiTokenAtom);
  const setSessionConversationSid = useSetAtom(sessionConversationSidAtom);
  const setSessionAppointmentShortId =
    useSetSessionAppointmentShortIdCallback();

  useEffect(
    function watchForRoomToken() {
      (async () => {
        if (!syncClient) return;
        logger.info("Initializing visitor-access document", {
          docSid: visitorAccessDocSid,
        });
        const visitorAccessDoc = await syncClient.document(visitorAccessDocSid);
        logger.info("Initialized visitor-access document", {
          docSid: visitorAccessDocSid,
        });
        visitorAccessDoc.on(
          "updated",
          ({
            data: {
              eventType,
              token,
              apiToken,
              conversationSid,
              deadline,
              zoomJwt,
            },
          }: {
            data: VisitorAccessDocData;
          }) => {
            logger.info("Received visitor-access document update", {
              hasToken: !!token,
              eventType,
              conversationSid,
              deadline,
            });
            if (deadline) {
              setDeadline(new Date(deadline));
            }
            setRoomToken(token);
            setZoomToken(zoomJwt);
            setApiToken(apiToken);
            setSessionConversationSid(conversationSid);
            switch (eventType) {
              case VisitorAccessDocEventType.GRANTED:
                if (!Statsig.checkGate(delayedEntryGate)) {
                  // This is not session blocking so fire and forget
                  setSessionAppointmentShortId();

                  transitionToMeetingRoom(conversationSid);
                }
                return;
              case VisitorAccessDocEventType.DENIED:
                // TODO: Handle denied event type
                return;
              default:
                logger.error("Received an unknown visitor-access event type", {
                  eventType,
                  visitorAccessDocSid,
                });
                return;
            }
          },
        );
      })();
    },
    [
      visitorUuid,
      syncClient,
      visitorAccessDocSid,
      transitionToMeetingRoom,
      setDeadline,
      setSessionConversationSid,
      setRoomToken,
      setApiToken,
      setSessionAppointmentShortId,
      setZoomToken,
    ],
  );
}
